import { useQuery } from '@tanstack/react-query';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import type { TrackSummary } from '../tracks/TrackList';
import { Artwork } from '../dashboard/Artwork';
import { TrackRail } from './TrackRail';
import { useDocumentTitle } from '../../lib/title';
import { ArtistHoverCard } from '../profile/ArtistHoverCard';
import { Verified } from '../../components/ui/Verified';

interface ChartTrack extends TrackSummary {
  rank: number;
  prev_rank: number | null;
  /** Positive is upward. Null means new to this chart. */
  movement: number | null;
  score: number;
}

interface Chart {
  chart: string;
  window: string;
  genre: string | null;
  computed_at: string | null;
  items: ChartTrack[];
  /**
   * The viewer's own tracks, when `discovery_self` is on.
   *
   * Deliberately not inside `items`: these are unranked and have not met the
   * listener floor, so putting them in a numbered list would need a fake rank
   * or would break the numbering. See `ShowChartAction::ownTracks`.
   */
  yours?: ChartTrack[];
}

interface Genre {
  id: number;
  name: string;
  slug: string;
}

/**
 * The windows, in the order they are offered.
 *
 * Trending first because it is the one that answers "what is happening now",
 * which is what somebody opening a discovery page is asking. The totals are
 * there for the artist checking their position, which is a different visit.
 */
const WINDOWS = [
  { id: 'trending', label: 'Trending' },
  { id: 'top24h', label: 'Today' },
  { id: 'top7d', label: 'This week' },
  { id: 'top30d', label: 'This month' },
  { id: 'topall', label: 'All time' },
] as const;

/**
 * Discover: the charts (HANDOFF §5).
 *
 * Reads a snapshot the cron computed, never a live aggregate - so two people
 * looking a minute apart see the same thing, which is what makes the movement
 * arrows mean anything at all.
 *
 * The window and genre live in the URL rather than in component state, because
 * "look at this week's house chart" is a thing somebody sends to a friend.
 */
export function DiscoverPage() {
  const [params, setParams] = useSearchParams();

  // Not named `window`: that shadows the global inside this component, and the
  // next person to reach for `window.scrollTo` gets a baffling error.
  const chartWindow = params.get('window') ?? 'trending';
  const genre = params.get('genre');

  const { data: genres } = useQuery({
    queryKey: ['genres'],
    queryFn: () => api.get<{ items: Genre[] }>('/genres'),
    staleTime: 60 * 60 * 1000,
  });

  useDocumentTitle('Discover');

  const { data, isPending, isError } = useQuery<Chart>({
    queryKey: ['chart', chartWindow, genre],
    queryFn: () => api.get<Chart>(`/charts/${chartWindow}${genre ? `?genre=${genre}` : ''}`),
    retry: false,
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);

    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setParams(next, { replace: true });
  };

  return (
    /*
      Wider than the old column.
      
      Rails need room to be rails: at `max-w-3xl` two sleeves and a sliver fit,
      which reads as a cramped list rather than something to browse. The ranked
      chart below is happy at any width.
    */
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['nav.discover']}</h1>
      <p className="mt-1.5 text-sm text-soft">
        {chartWindow === 'trending'
          ? 'What is moving right now: plays and favorites, weighted against how long a track has been out.'
          : 'Most played, by window.'}
      </p>

      {/* --------------------------------------------------------------- */}
      {/* Which chart                                                      */}
      {/* --------------------------------------------------------------- */}
      <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line pb-3">
        {WINDOWS.map((option) => (
          <button
            key={option.id}
            role="tab"
            type="button"
            aria-selected={chartWindow === option.id}
            data-window={option.id}
            onClick={() => setParam('window', option.id)}
            className={`rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
              chartWindow === option.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <GenrePill active={genre === null} onClick={() => setParam('genre', null)}>
          Everything
        </GenrePill>
        {(genres?.items ?? []).map((option) => (
          <GenrePill
            key={option.id}
            active={genre === option.slug}
            onClick={() => setParam('genre', option.slug)}
          >
            {option.name}
          </GenrePill>
        ))}
      </div>

      {/* --------------------------------------------------------------- */}
      {/* The chart                                                        */}
      {/* --------------------------------------------------------------- */}
      {isPending ? (
        <div className="mt-6 flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">That chart could not be loaded.</p>
        </Card>
      ) : data.items.length === 0 ? (
        <>
          {/*
            An empty chart used to be an empty page, which on a young catalogue
            is most of them - and "nothing here" is the worst thing a discovery
            page can say to somebody who came to find music. The floor is still
            explained, because it is the reason, but it is a note above real
            records rather than the whole screen.
          */}
          <p className="mt-6 text-sm text-soft">
            Nothing has charted here yet. A track needs {' '}
            <span className="text-ink">a handful of separate listeners</span> first, which is what
            stops anybody putting themselves at the top. In the meantime:
          </p>

          <YourRail chart={data} />
          <Fallback />
        </>
      ) : (
        <>
          {/*
            The rails come first, and they are all derived from the chart that
            has already been fetched - no extra request. What somebody scanning
            a discovery page wants is "what is moving" and "what is new", which
            are two questions a single ranked list answers badly: the climber at
            number nine is the interesting one and the list buries it.
          */}
          <TrackRail
            id="climbing"
            title="Climbing fastest"
            hint="biggest jump since the last count"
            tracks={climbing(data.items)}
          />

          <TrackRail
            id="new"
            title="New here"
            hint="first appearance on this chart"
            tracks={data.items.filter((track) => track.movement === null).slice(0, 12)}
            badge="none"
          />

          <YourRail chart={data} />

          <h2 className="mt-10 text-lg font-semibold tracking-tight text-ink">The chart</h2>
          <ChartList chart={data} />
          {data.computed_at && (
            /*
              Said plainly, because the page is not live and pretending
              otherwise would make somebody refresh it waiting for a number
              to move.
            */
            <p className="mt-4 text-xs text-faint">
              Updated {relative(data.computed_at)}. Charts are recomputed every hour.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The climbers, best first.
 *
 * Sorted by how far a track moved rather than where it landed, which is the
 * whole point of the rail - number nine that came from thirty is a better story
 * than number two that has sat there a fortnight, and the ranked list below
 * tells the second story perfectly well already.
 */
function climbing(items: ChartTrack[]): ChartTrack[] {
  return items
    .filter((track) => (track.movement ?? 0) > 0)
    .sort((a, b) => (b.movement ?? 0) - (a.movement ?? 0))
    .slice(0, 12);
}

/**
 * The viewer's own tracks, when the flag is on and they have any.
 *
 * Renders nothing at all otherwise - including for signed-out visitors, who
 * never receive the key. The wording matters more than usual here: this is not
 * a chart position and must not be mistaken for one, so it says where it stands
 * rather than leaving somebody to assume.
 */
function YourRail({ chart }: { chart: Chart }) {
  const yours = chart.yours ?? [];

  if (yours.length === 0) {
    return null;
  }

  return (
    <TrackRail
      id="yours"
      title="Yours"
      hint="only you see these, not ranked"
      tracks={yours}
      badge="yours"
    />
  );
}

/**
 * What to show when nothing has charted.
 *
 * Real tracks, newest first, from the endpoint the dashboard already uses - so
 * this costs no new backend and cannot drift from what "new" means elsewhere.
 * Labelled as what it is: an empty chart dressed up as a chart would be worse
 * than the empty page it replaced.
 */
function Fallback() {
  const { data } = useQuery({
    queryKey: ['dashboard-tracks', 'all'],
    queryFn: () => api.get<{ items: TrackSummary[] }>('/dashboard/tracks?tab=all'),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <TrackRail
      id="fresh"
      title="Fresh out"
      hint="newest on the platform, not ranked"
      tracks={data?.items ?? []}
      badge="none"
    />
  );
}

function ChartList({ chart }: { chart: Chart }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  return (
    <ol className="mt-6 flex flex-col gap-2" data-chart={chart.chart}>
      {chart.items.map((track) => {
        const isCurrent = current?.id === track.id;

        return (
          <li key={track.id} data-chart-row={track.id} data-rank={track.rank}>
            <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="flex w-10 shrink-0 flex-col items-center">
                <span className="text-sm font-semibold tabular-nums text-ink">{track.rank}</span>
                <Movement movement={track.movement} />
              </div>

              <button
                type="button"
                aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                onClick={() => (isCurrent ? toggle() : void play(track, chart.items, 'chart'))}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
              >
                {isCurrent && playing ? (
                  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <rect x="4" y="2.5" width="3" height="11" rx="1" />
                    <rect x="9" y="2.5" width="3" height="11" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                    <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                  </svg>
                )}
              </button>

              <Artwork
                track={track}
                className="hidden h-11 w-11 shrink-0 rounded-[var(--radius-control)] sm:block"
              />

              <div className="min-w-0 flex-1">
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className="block truncate text-sm font-medium text-ink hover:underline"
                >
                  {track.title}
                </DrawerLink>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {track.owner && (
                    <ArtistHoverCard username={track.owner.username}>
                      <Link to={`/${track.owner.username}`} className="meta-caps hover:text-ink">
                        {track.owner.display_name || `@${track.owner.username}`}
                        {track.owner?.verified && <Verified />}
                      </Link>
                    </ArtistHoverCard>
                  )}
                  <span className="meta-caps">
                    {track.play_count.toLocaleString()} {track.play_count === 1 ? 'play' : 'plays'}
                  </span>
                  {track.movement === null && <Chip tone="fresh">{copy['label.newHere']}</Chip>}
                </div>
              </div>

              <FavoriteButton
                trackId={track.id}
                favorited={track.favorited}
                likeCount={track.like_count}
                size="sm"
              />

              <AddToPlaylist trackId={track.id} title={track.title} size="sm" />
            </Card>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * ▲7 / ▼3 / — , or nothing at all for a new entry.
 *
 * §5 calls this a small detail with disproportionate pull, and it is right:
 * artists check their position obsessively and the delta is what makes a chart
 * feel alive rather than like a table.
 *
 * The arrow carries a text label for screen readers, because a triangle and a
 * number is not a sentence.
 */
function Movement({ movement }: { movement: number | null }) {
  if (movement === null) {
    return <span className="sr-only">New entry</span>;
  }

  if (movement === 0) {
    return (
      <span className="meta-caps text-faint" aria-label="No change">
        —
      </span>
    );
  }

  const up = movement > 0;

  return (
    <span
      className={`meta-caps tabular-nums ${up ? 'text-positive' : 'text-negative'}`}
      aria-label={`${up ? 'Up' : 'Down'} ${Math.abs(movement)}`}
    >
      <span aria-hidden="true">
        {up ? '▲' : '▼'}
        {Math.abs(movement)}
      </span>
    </span>
  );
}

function GenrePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-[var(--radius-pill)] border px-3 py-1.5 text-xs transition-colors ${
        active
          ? 'border-line-lit bg-raised text-ink'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** "20 minutes ago". Enough precision for something that updates hourly. */
function relative(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);

  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}
